import { Head, router } from "@inertiajs/react"
import { useState } from "react"
import { generateUUID } from "@/api/conversations"

import EmptyState from "@/components/conversation/empty-state"
import InputBar from "@/components/conversation/input-bar"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Conversation",
    href: "/conversations",
  },
]

export default function ConversationNew() {
  const [isProcessingAudio, setIsProcessingAudio] = useState(false)

  // Handle sending a new message
  const handleSend = (text: string) => {
    const uuid = generateUUID()
    sessionStorage.setItem(`pending_message_${uuid}`, text)
    router.visit(`/conversations/${uuid}`)
  }

  const handleAudioRecorded = async (blob: Blob) => {
    if (isProcessingAudio) return
    setIsProcessingAudio(true)

    try {
      const formData = new FormData()
      formData.append("audio", blob, "recording.webm")

      // Get CSRF token
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content")

      const response = await fetch("/api/transcribe", {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken ?? "",
        },
        body: formData,
      })

      if (!response.ok) {
        throw new Error("Failed to transcribe audio")
      }

      const data = await response.json()
      if (data.text) {
        handleSend(data.text)
      }
    } catch (error) {
      console.error("Transcription failed:", error)
      alert("Failed to transcribe audio. Please try again.")
    } finally {
      setIsProcessingAudio(false)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Conversation" />

      {/* Main conversation area - full height flex container */}
      <div className="flex h-full flex-1 flex-col">
        {/* Conditionally render empty state or message list */}
        <EmptyState />

        {/* Input bar sticks to bottom */}
        <InputBar
          onSend={handleSend}
          onAudioRecorded={(blob) => {
            handleAudioRecorded(blob).catch((error) =>
              console.error("Audio recording error:", error),
            )
          }}
          isProcessingAudio={isProcessingAudio}
        />
      </div>
    </AppLayout>
  )
}
